import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { toast } from "sonner"
import { useTranslation } from "@/hooks/use-translation"
import { GameLayout, GameStatusAlert } from '@/components/games/common'
import { GameShortcuts } from '@/components/games/common/game-shortcuts'
import { Grid3x3 } from 'lucide-react'
import { getDictionary, getDateString, removeAccents, generateDateSeed } from '@/lib/dictionaries'
import { saveGameResult, getGameResult, hasPlayedToday } from '@/lib/game-stats'
import { InContentAd } from '@/components/adsense'

interface GameState {
  grid: string[][]
  selectedCells: { row: number; col: number }[]
  foundWords: string[]
  foundWordPositions: { [word: string]: { row: number; col: number }[] }
  targetWords: string[]
  attempts: number
  gameStatus: 'playing' | 'won' | 'lost'
  startTime: number
  endTime: number | null
}

const GRID_SIZE = 10
const MAX_ATTEMPTS = 20
const WORDS_TO_FIND = 5

// Función para obtener múltiples palabras diferentes del día
function getDailyWordsSoup(language: string, date: Date, count: number): string[] {
  const dictionary = getDictionary(language)
  const baseSeed = generateDateSeed(date)
  const words: string[] = []
  const usedIndices = new Set<number>()
  
  let seed = baseSeed
  for (let i = 0; i < count; i++) {
    // Usar un seed diferente para cada palabra
    seed = (seed * 9301 + 12345 + i * 1000) % 982451653
    let wordIndex = seed % dictionary.targetWords.length
    
    // Evitar palabras duplicadas
    let attempts = 0
    while (usedIndices.has(wordIndex) && attempts < 100) {
      seed = (seed * 9301 + 49297) % 982451653
      wordIndex = seed % dictionary.targetWords.length
      attempts++
    }
    
    usedIndices.add(wordIndex)
    words.push(dictionary.targetWords[wordIndex])
  }
  
  return words
}

// Función para generar letra aleatoria basada en seed
function getRandomLetter(seed: number): string {
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
  const rng = (seed * 9301 + 49297) % 233280
  return letters[Math.floor((rng / 233280) * letters.length)]
}

// Función para generar la sopa de letras con múltiples palabras
function generateWordSoup(targetWords: string[], date: Date, language: string): string[][] {
  const grid: string[][] = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(''))
  
  // Crear seed determinístico
  const dateString = getDateString(date)
  let seed = 0
  const seedString = dateString + language + 'sopa'
  for (let i = 0; i < seedString.length; i++) {
    seed = ((seed << 5) - seed + seedString.charCodeAt(i)) & 0xffffffff
  }
  seed = Math.abs(seed)
  
  // Función RNG determinística
  let rngState = seed
  const rng = () => {
    rngState = (rngState * 9301 + 49297) % 233280
    return rngState / 233280
  }
  
  // Direcciones: horizontal, vertical, diagonal (en ambas direcciones)
  const directions = [
    { dr: 0, dc: 1 },   // Horizontal (izquierda a derecha)
    { dr: 0, dc: -1 },  // Horizontal inversa (derecha a izquierda)
    { dr: 1, dc: 0 },   // Vertical (arriba a abajo)
    { dr: -1, dc: 0 },  // Vertical inversa (abajo a arriba)
    { dr: 1, dc: 1 },   // Diagonal \ (arriba-izquierda a abajo-derecha)
    { dr: -1, dc: -1 }, // Diagonal \ inversa (abajo-derecha a arriba-izquierda)
    { dr: 1, dc: -1 },  // Diagonal / (arriba-derecha a abajo-izquierda)
    { dr: -1, dc: 1 },  // Diagonal / inversa (abajo-izquierda a arriba-derecha)
  ]
  
  const placedWords: Array<{ word: string; positions: { row: number; col: number }[] }> = []
  
  // Colocar cada palabra
  for (const word of targetWords) {
    let placed = false
    let attempts = 0
    
    while (!placed && attempts < 200) {
      const direction = directions[Math.floor(rng() * directions.length)]
      
      // Calcular posición inicial válida asegurando que toda la palabra quepa
      const maxRow = GRID_SIZE - Math.abs(direction.dr * (word.length - 1))
      const maxCol = GRID_SIZE - Math.abs(direction.dc * (word.length - 1))
      
      if (maxRow <= 0 || maxCol <= 0) {
        attempts++
        continue
      }
      
      // Calcular posición inicial válida considerando la dirección
      let startRow: number
      let startCol: number
      
      if (direction.dr < 0) {
        // Dirección vertical o diagonal hacia arriba
        startRow = word.length - 1 + Math.floor(rng() * (GRID_SIZE - word.length + 1))
        if (startRow >= GRID_SIZE) {
          attempts++
          continue
        }
      } else {
        startRow = Math.floor(rng() * maxRow)
      }
      
      if (direction.dc < 0) {
        // Dirección horizontal o diagonal hacia la izquierda
        startCol = word.length - 1 + Math.floor(rng() * (GRID_SIZE - word.length + 1))
        if (startCol >= GRID_SIZE) {
          attempts++
          continue
        }
      } else {
        startCol = Math.floor(rng() * maxCol)
      }
      
      // Verificar si la posición es válida (no se solapa con otras palabras)
      const positions: { row: number; col: number }[] = []
      let canPlace = true
      
      for (let i = 0; i < word.length; i++) {
        const row = startRow + direction.dr * i
        const col = startCol + direction.dc * i
        
        // Verificar límites
        if (row < 0 || row >= GRID_SIZE || col < 0 || col >= GRID_SIZE) {
          canPlace = false
          break
        }
        
        // Verificar si la celda está ocupada por otra palabra (solo si la letra es diferente)
        if (grid[row][col] !== '' && grid[row][col] !== word[i]) {
          canPlace = false
          break
        }
        
        positions.push({ row, col })
      }
      
      if (canPlace) {
        // Colocar la palabra
        for (let i = 0; i < word.length; i++) {
          const { row, col } = positions[i]
          grid[row][col] = word[i]
        }
        placedWords.push({ word, positions })
        placed = true
      }
      
      attempts++
    }
  }
  
  // Rellenar el resto con letras aleatorias
  for (let row = 0; row < GRID_SIZE; row++) {
    for (let col = 0; col < GRID_SIZE; col++) {
      if (grid[row][col] === '') {
        grid[row][col] = getRandomLetter(rngState + row * GRID_SIZE + col)
      }
    }
  }
  
  return grid
}

export function SopaDeLetras() {
  const { t, language } = useTranslation()
  const today = getDateString()
  const [year, month, day] = today.split('-').map(Number)
  const todayDate = new Date(year, month - 1, day)
  const targetWords = getDailyWordsSoup(language, todayDate, WORDS_TO_FIND)
  const grid = generateWordSoup(targetWords, todayDate, language)

  const [gameState, setGameState] = useState<GameState>({
    grid,
    selectedCells: [],
    foundWords: [],
    foundWordPositions: {},
    targetWords,
    attempts: 0,
    gameStatus: 'playing',
    startTime: Date.now(),
    endTime: null
  })

  const [, setIsNewGameMode] = useState(false)
  const [showFirstTimeModal, setShowFirstTimeModal] = useState(false)
  const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false)

  // Función para guardar el estado parcial del juego
  const savePartialGameState = (state: GameState) => {
    if (state.gameStatus === 'playing') {
      const partialState = {
        selectedCells: state.selectedCells,
        attempts: state.attempts,
        foundWords: state.foundWords,
        foundWordPositions: state.foundWordPositions,
        targetWords: state.targetWords,
        startTime: state.startTime,
        date: today
      }
      localStorage.setItem(`game-sopa-de-letras-${language}-partial`, JSON.stringify(partialState))
    }
  }

  // Función para cargar el estado parcial del juego
  const loadPartialGameState = (): Partial<GameState> | null => {
    const key = `game-sopa-de-letras-${language}-partial`
    const stored = localStorage.getItem(key)
    if (stored) {
      try {
        const parsed = JSON.parse(stored)
        if (parsed.date === today) {
          return parsed
        } else {
          localStorage.removeItem(key)
          return null
        }
      } catch {
        return null
      }
    }
    return null
  }

  // Función para limpiar el estado parcial
  const clearPartialGameState = () => {
    localStorage.removeItem(`game-sopa-de-letras-${language}-partial`)
  }

  // Función para buscar una palabra en la cuadrícula y devolver sus posiciones
  const findWordInGrid = (word: string, gridToSearch: string[][]): { row: number; col: number }[] | null => {
    const directions = [
      { dr: 0, dc: 1 },   // Horizontal (izquierda a derecha)
      { dr: 0, dc: -1 },  // Horizontal inversa (derecha a izquierda)
      { dr: 1, dc: 0 },   // Vertical (arriba a abajo)
      { dr: -1, dc: 0 },  // Vertical inversa (abajo a arriba)
      { dr: 1, dc: 1 },   // Diagonal \ (arriba-izquierda a abajo-derecha)
      { dr: -1, dc: -1 }, // Diagonal \ inversa (abajo-derecha a arriba-izquierda)
      { dr: 1, dc: -1 },  // Diagonal / (arriba-derecha a abajo-izquierda)
      { dr: -1, dc: 1 },  // Diagonal / inversa (abajo-izquierda a arriba-derecha)
    ]

    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        for (const direction of directions) {
          const positions: { row: number; col: number }[] = []
          let matches = true

          for (let i = 0; i < word.length; i++) {
            const checkRow = row + direction.dr * i
            const checkCol = col + direction.dc * i

            if (checkRow < 0 || checkRow >= GRID_SIZE || checkCol < 0 || checkCol >= GRID_SIZE) {
              matches = false
              break
            }

            if (removeAccents(gridToSearch[checkRow][checkCol]) !== removeAccents(word[i])) {
              matches = false
              break
            }

            positions.push({ row: checkRow, col: checkCol })
          }

          if (matches) {
            return positions
          }
        }
      }
    }

    return null
  }

  useEffect(() => {
    setIsInitialLoadComplete(false)

    if (hasPlayedToday(`sopa-de-letras-${language}`, today)) {
      const todayResult = getGameResult(`sopa-de-letras-${language}`, today)
      if (todayResult) {
        // Recalcular las posiciones de las palabras encontradas
        const foundWordsList = todayResult.won ? targetWords : (todayResult.attempts || [])
        const foundWordPositions: { [word: string]: { row: number; col: number }[] } = {}
        
        // Buscar cada palabra encontrada en la cuadrícula
        for (const word of foundWordsList) {
          const positions = findWordInGrid(word, grid)
          if (positions) {
            foundWordPositions[word] = positions
          }
        }
        
        setGameState({
          grid,
          selectedCells: [],
          foundWords: foundWordsList,
          foundWordPositions,
          targetWords,
          attempts: todayResult.attemptsCount || 0,
          gameStatus: todayResult.won ? 'won' : 'lost',
          startTime: Date.now(),
          endTime: todayResult.won ? Date.now() : null
        })
        clearPartialGameState()
      }
    } else {
      const partialState = loadPartialGameState()
      if (partialState) {
        setGameState(prev => ({
          ...prev,
          selectedCells: partialState.selectedCells || [],
          attempts: partialState.attempts || 0,
          foundWords: partialState.foundWords || [],
          foundWordPositions: partialState.foundWordPositions || {},
          targetWords: partialState.targetWords || targetWords
        }))
      }
    }

    const hasSeenInstructions = localStorage.getItem(`game-sopa-de-letras-${language}-instructions`)
    if (!hasSeenInstructions && window.innerWidth < 1024) {
      setShowFirstTimeModal(true)
    }

    setIsInitialLoadComplete(true)
  }, [language, today])

  useEffect(() => {
    if (!isInitialLoadComplete) return

    if (gameState.gameStatus === 'playing') {
      savePartialGameState(gameState)
    }
  }, [gameState, isInitialLoadComplete])

  const handleCellClick = (row: number, col: number) => {
    if (gameState.gameStatus !== 'playing') return
    
    // Verificar si todas las palabras ya fueron encontradas
    if (gameState.foundWords.length >= gameState.targetWords.length) return

    const cellIndex = gameState.selectedCells.findIndex(
      c => c.row === row && c.col === col
    )

    if (cellIndex !== -1) {
      // Deseleccionar celda y todas las siguientes
      setGameState(prev => ({
        ...prev,
        selectedCells: prev.selectedCells.slice(0, cellIndex)
      }))
    } else {
      let newSelected: { row: number; col: number }[]
      
      if (gameState.selectedCells.length === 0) {
        // Primera celda seleccionada
        newSelected = [{ row, col }]
      } else {
        const lastCell = gameState.selectedCells[gameState.selectedCells.length - 1]
        const dr = row - lastCell.row
        const dc = col - lastCell.col
        
        // Verificar que sea adyacente (exactamente 1 celda de distancia)
        const isAdjacent = Math.abs(dr) <= 1 && Math.abs(dc) <= 1 && !(dr === 0 && dc === 0)
        
        if (!isAdjacent) {
          // Si no es adyacente, empezar nueva selección
          newSelected = [{ row, col }]
        } else {
          // Verificar que esté en línea recta (horizontal, vertical o diagonal)
          const isStraightLine = (dr === 0 || dc === 0 || Math.abs(dr) === Math.abs(dc))
          
          if (!isStraightLine) {
            // No está en línea recta, empezar nueva selección
            newSelected = [{ row, col }]
          } else {
            // Verificar que la dirección sea consistente con las anteriores
            if (gameState.selectedCells.length > 1) {
              const firstCell = gameState.selectedCells[0]
              const secondCell = gameState.selectedCells[1]
              const baseDr = secondCell.row - firstCell.row
              const baseDc = secondCell.col - firstCell.col
              
              // Normalizar dirección base (solo signo, no magnitud)
              const normBaseDr = baseDr === 0 ? 0 : baseDr / Math.abs(baseDr)
              const normBaseDc = baseDc === 0 ? 0 : baseDc / Math.abs(baseDc)
              
              // Normalizar dirección nueva
              const normDr = dr === 0 ? 0 : dr / Math.abs(dr)
              const normDc = dc === 0 ? 0 : dc / Math.abs(dc)
              
              if (normDr !== normBaseDr || normDc !== normBaseDc) {
                // Dirección diferente, empezar nueva selección
                newSelected = [{ row, col }]
              } else {
                // Misma dirección, agregar celda
                newSelected = [...gameState.selectedCells, { row, col }]
              }
            } else {
              // Solo hay una celda, agregar la segunda
              newSelected = [...gameState.selectedCells, { row, col }]
            }
          }
        }
      }
      
      // Limitar a 5 celdas máximo
      if (newSelected.length > 5) {
        return
      }
      
      setGameState(prev => ({
        ...prev,
        selectedCells: newSelected
      }))

      // Verificar si la palabra está completa
      if (newSelected.length === 5) {
        // Validar que todas las celdas estén en línea recta continua sin espacios
        const isValidLine = (() => {
          if (newSelected.length < 2) return true
          
          const first = newSelected[0]
          const last = newSelected[newSelected.length - 1]
          const dr = last.row - first.row
          const dc = last.col - first.col
          
          // Calcular la dirección normalizada
          const stepRow = dr === 0 ? 0 : dr / Math.abs(dr)
          const stepCol = dc === 0 ? 0 : dc / Math.abs(dc)
          
          // Verificar que todas las celdas estén en la línea recta continua
          for (let i = 0; i < newSelected.length; i++) {
            const expectedRow = first.row + stepRow * i
            const expectedCol = first.col + stepCol * i
            const actual = newSelected[i]
            
            if (actual.row !== expectedRow || actual.col !== expectedCol) {
              return false
            }
          }
          
          return true
        })()
        
        if (!isValidLine) {
          // Las celdas no están en línea recta continua, limpiar selección
          setGameState(prev => ({
            ...prev,
            selectedCells: [],
            attempts: prev.attempts + 1
          }))
          toast.error(t("games.sopaDeLetras.invalidSelection"))
          return
        }
        
        const selectedWord = newSelected
          .map(cell => gameState.grid[cell.row][cell.col])
          .join('')
        const accentFreeWord = removeAccents(selectedWord)

        // Verificar si es una de las palabras objetivo
        const foundWordIndex = gameState.targetWords.findIndex(
          word => removeAccents(word) === accentFreeWord
        )

        if (foundWordIndex !== -1 && !gameState.foundWords.includes(gameState.targetWords[foundWordIndex])) {
          // Palabra encontrada - guardar las posiciones
          const foundWord = gameState.targetWords[foundWordIndex]
          const wordPositions = [...newSelected] // Copiar las posiciones de las celdas seleccionadas
          
          const newFoundWords = [...gameState.foundWords, foundWord]
          const newFoundWordPositions = {
            ...gameState.foundWordPositions,
            [foundWord]: wordPositions
          }
          
          setGameState(prev => ({
            ...prev,
            selectedCells: [],
            foundWords: newFoundWords,
            foundWordPositions: newFoundWordPositions,
            attempts: prev.attempts + 1
          }))

          toast.success(t("games.sopaDeLetras.wordFound", { word: gameState.targetWords[foundWordIndex] }), {
            duration: 3000
          })

          // Verificar si todas las palabras fueron encontradas
          if (newFoundWords.length >= gameState.targetWords.length) {
            const endTime = Date.now()
            const timeSpent = Math.floor((endTime - gameState.startTime) / 1000)
            
            saveGameResult(`sopa-de-letras-${language}`, newFoundWords, true, today, {
              time: timeSpent
            })
            setIsNewGameMode(false)
            clearPartialGameState()

            setGameState(prev => ({
              ...prev,
              gameStatus: 'won',
              endTime
            }))

            toast.success(t("games.sopaDeLetras.congratulations"), {
              duration: 4000
            })
          }
        } else {
          // Palabra incorrecta o ya encontrada
          setGameState(prev => ({
            ...prev,
            selectedCells: [],
            attempts: prev.attempts + 1
          }))

          if (gameState.attempts + 1 >= MAX_ATTEMPTS) {
            const endTime = Date.now()
            saveGameResult(`sopa-de-letras-${language}`, gameState.foundWords, false, today)
            clearPartialGameState()

            setGameState(prev => ({
              ...prev,
              gameStatus: 'lost',
              endTime
            }))

            toast.error(t("games.sopaDeLetras.gameOver"), {
              duration: 4000
            })
          } else {
            toast.error(t("games.sopaDeLetras.wrongWord"))
          }
        }
      }
    }
  }

  const handleClear = () => {
    if (gameState.gameStatus !== 'playing') return
    if (gameState.foundWords.length >= gameState.targetWords.length) return
    setGameState(prev => ({
      ...prev,
      selectedCells: []
    }))
  }

  const handleCloseFirstTimeModal = () => {
    setShowFirstTimeModal(false)
    localStorage.setItem(`game-sopa-de-letras-${language}-instructions`, 'true')
  }

  const isCellSelected = (row: number, col: number) => {
    return gameState.selectedCells.some(c => c.row === row && c.col === col)
  }

  const isCellInPath = (row: number, col: number) => {
    if (gameState.selectedCells.length < 2) return false
    
    // Verificar si la celda está en línea recta con las seleccionadas
    const first = gameState.selectedCells[0]
    const last = gameState.selectedCells[gameState.selectedCells.length - 1]
    
    const dr = last.row - first.row
    const dc = last.col - first.col
    
    if (dr === 0 && dc === 0) return false
    
    const stepRow = dr === 0 ? 0 : dr / Math.abs(dr)
    const stepCol = dc === 0 ? 0 : dc / Math.abs(dc)
    
    for (let i = 0; i <= Math.max(Math.abs(dr), Math.abs(dc)); i++) {
      const checkRow = first.row + stepRow * i
      const checkCol = first.col + stepCol * i
      if (checkRow === row && checkCol === col) return true
    }
    
    return false
  }

  const isWordFound = (word: string) => {
    return gameState.foundWords.includes(word)
  }

  const isCellInFoundWord = (row: number, col: number) => {
    // Verificar si esta celda pertenece a alguna palabra encontrada
    for (const word of gameState.foundWords) {
      const positions = gameState.foundWordPositions[word]
      if (positions && positions.some(pos => pos.row === row && pos.col === col)) {
        return true
      }
    }
    return false
  }

  const renderGameContent = () => (
    <>
      {/* Game Status */}
      {gameState.gameStatus !== 'playing' && (
        <GameStatusAlert status={gameState.gameStatus}>
          <div className="space-y-1">
            <div className="font-bold">
              {gameState.gameStatus === 'won'
                ? t("games.sopaDeLetras.youWon", { attempts: gameState.attempts, words: gameState.foundWords.length })
                : t("games.sopaDeLetras.youLost", { words: gameState.foundWords.length, total: gameState.targetWords.length })
              }
            </div>
            <div className="text-xs font-medium">
              {t("games.sopaDeLetras.comeBackTomorrow")}
            </div>
          </div>
        </GameStatusAlert>
      )}

      {/* Words to Find */}
      <div className="mb-4">
        <p className="text-sm font-medium text-center mb-2">{t("games.sopaDeLetras.wordsToFind")}</p>
        <div className="flex flex-wrap gap-2 justify-center">
          {gameState.targetWords.map((word, index) => (
            <Badge
              key={index}
              variant={isWordFound(word) ? "default" : "outline"}
              className={`text-sm ${isWordFound(word) ? 'bg-primary text-primary-foreground line-through' : ''}`}
            >
              {word}
            </Badge>
          ))}
        </div>
      </div>

      {/* Attempts Counter */}
      {gameState.gameStatus === 'playing' && (
        <div className="text-center mb-4">
          <Badge variant="outline" className="text-sm">
            {t("games.sopaDeLetras.attempts")}: {gameState.attempts} / {MAX_ATTEMPTS} | 
            {t("games.sopaDeLetras.found")}: {gameState.foundWords.length} / {gameState.targetWords.length}
          </Badge>
        </div>
      )}

      {/* Word Soup Grid */}
      <div className="mb-6 flex justify-center">
        <div className="grid gap-1 p-4 bg-muted rounded-lg" style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, minmax(0, 1fr))` }}>
          {gameState.grid.map((row, rowIndex) =>
            row.map((letter, colIndex) => {
              const isSelected = isCellSelected(rowIndex, colIndex)
              const isInPath = isCellInPath(rowIndex, colIndex) && gameState.selectedCells.length > 0
              const isFound = isCellInFoundWord(rowIndex, colIndex)
              
              return (
                <button
                  key={`${rowIndex}-${colIndex}`}
                  className={`
                    w-8 h-8 lg:w-10 lg:h-10
                    flex items-center justify-center
                    text-sm lg:text-base font-bold
                    rounded transition-all
                    ${isFound
                      ? 'bg-primary text-primary-foreground ring-2 ring-primary ring-offset-1'
                      : isSelected
                        ? 'bg-primary text-primary-foreground scale-110'
                        : isInPath
                          ? 'bg-primary/30 text-primary-foreground'
                          : 'bg-background hover:bg-accent text-foreground'
                    }
                    ${gameState.gameStatus === 'playing' && gameState.foundWords.length < gameState.targetWords.length ? 'cursor-pointer' : 'cursor-default'}
                  `}
                  onClick={() => handleCellClick(rowIndex, colIndex)}
                  disabled={gameState.gameStatus !== 'playing' || gameState.foundWords.length >= gameState.targetWords.length}
                >
                  {letter}
                </button>
              )
            })
          )}
        </div>
      </div>

      {/* Selected Word */}
      {gameState.selectedCells.length > 0 && gameState.gameStatus === 'playing' && gameState.foundWords.length < gameState.targetWords.length && (
        <div className="mb-4 text-center">
          <p className="text-sm text-muted-foreground mb-2">{t("games.sopaDeLetras.selectedWord")}</p>
          <div className="flex gap-2 justify-center items-center">
            <div className="text-lg font-bold">
              {gameState.selectedCells.map(cell => gameState.grid[cell.row][cell.col]).join('')}
            </div>
            <Button onClick={handleClear} variant="outline" size="sm">
              {t("games.sopaDeLetras.clear")}
            </Button>
          </div>
        </div>
      )}

      {/* Instructions */}
      {gameState.gameStatus === 'playing' && gameState.selectedCells.length === 0 && gameState.foundWords.length < gameState.targetWords.length && (
        <div className="text-center text-sm text-muted-foreground">
          {t("games.sopaDeLetras.instructions")}
        </div>
      )}
    </>
  )

  return (
    <GameLayout>
      {/* Desktop Layout */}
      <div className="hidden lg:flex lg:flex-row lg:gap-8 lg:px-8 lg:py-8">
        <div className="flex-1 flex flex-col justify-center items-center">
          <div className="w-full max-w-xl xl:max-w-2xl">
            {renderGameContent()}
          </div>
        </div>

        {/* Sidebar */}
        <div className="w-80 min-w-80 space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-sm">
                <Grid3x3 className="h-4 w-4" />
                {t("games.sopaDeLetras.howToPlay")}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-xs">{t("games.sopaDeLetras.rules.main")}</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary text-primary-foreground text-xs">{t("games.sopaDeLetras.rules.step1")}</Badge>
                  <span className="text-xs">{t("games.sopaDeLetras.rules.step1Desc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-secondary text-secondary-foreground text-xs">{t("games.sopaDeLetras.rules.step2")}</Badge>
                  <span className="text-xs">{t("games.sopaDeLetras.rules.step2Desc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-muted text-muted-foreground text-xs">{t("games.sopaDeLetras.rules.step3")}</Badge>
                  <span className="text-xs">{t("games.sopaDeLetras.rules.step3Desc")}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <InContentAd className="w-full" compact />
          <GameShortcuts currentGame="sopa-de-letras" />
        </div>
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden min-h-[calc(100dvh-64px)] flex flex-col">
        <InContentAd className="w-full" compact />

        <div className="flex-1 flex flex-col py-2 overflow-y-auto">
          <div className="w-full py-4">
            {renderGameContent()}

            {(gameState.gameStatus === 'won' || gameState.gameStatus === 'lost') && (
              <GameShortcuts currentGame="sopa-de-letras" />
            )}
          </div>
        </div>
      </div>

      {/* Modal de primera vez - Solo móvil */}
      <Dialog open={showFirstTimeModal} onOpenChange={setShowFirstTimeModal}>
        <DialogContent className="max-w-sm mx-auto mt-20">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Grid3x3 className="h-5 w-5" />
              {t("games.sopaDeLetras.howToPlay")}
            </DialogTitle>
            <DialogDescription className="text-left space-y-3">
              <p className="text-sm">{t("games.sopaDeLetras.rules.main")}</p>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge className="bg-primary text-primary-foreground text-xs">{t("games.sopaDeLetras.rules.step1")}</Badge>
                  <span className="text-xs">{t("games.sopaDeLetras.rules.step1Desc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-secondary text-secondary-foreground text-xs">{t("games.sopaDeLetras.rules.step2")}</Badge>
                  <span className="text-xs">{t("games.sopaDeLetras.rules.step2Desc")}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="bg-muted text-muted-foreground text-xs">{t("games.sopaDeLetras.rules.step3")}</Badge>
                  <span className="text-xs">{t("games.sopaDeLetras.rules.step3Desc")}</span>
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end pt-4">
            <Button onClick={handleCloseFirstTimeModal}>
              {t("navigation.gotIt")}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </GameLayout>
  )
}
